'use client';

import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { ChangeEvent } from 'react';
import { DebounceInput } from 'react-debounce-input';

import { TYPE_CONFIG } from '@/modules/players/v2/tabs/constants';
import { ModuleType } from '@/types/players.types';

interface FilterTabsProps {
  hasData: boolean;
  type: ModuleType;
}

export function FilterTabs({ hasData, type }: FilterTabsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const search = searchParams?.get('search') ?? '';

  const config = TYPE_CONFIG[type];

  const showSearchInput = (hasData || search) && config.searchPlaceholder ? true : false;
  const searchParamsType = searchParams.get(config.typeParamKey) || '';
  const statusValue = config.statusKey ? searchParams.get(config.statusKey) : null;

  const statusItems = config.statusItems ?? [];

  const handleStatusChange = (newStatus: string) => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());
    if (config.statusKey) {
      newSearchParams.set(config.statusKey, newStatus);
    }

    if (type === 'tournament' || type === 'teamLeague' || type === 'league') {
      newSearchParams.set('page', '1');
    }

    router.replace(`?${newSearchParams.toString()}`);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\s+/g, ' ');
    const newSearchParams = new URLSearchParams(searchParams?.toString());
    newSearchParams.set('search', cleanedValue.trim());
    router.replace(`?${newSearchParams?.toString()}`);
  };

  return (
    <div className='flex flex-col gap-6 sm:gap-10 w-full items-center'>
      <div className='flex flex-col sm:flex-row w-full gap-6 sm:gap-3'>
        {statusItems.length > 0 && searchParamsType !== 'favorites' ? (
          <ul className='flex items-center gap-3'>
            {statusItems.map((item) => {
              return (
                <li key={`filter-item-${item.name}`}>
                  <Button
                    variant='secondary'
                    size='md'
                    className={clsx(
                      'flex w-full justify-center text-sm font-semibold rounded-md px-4 p-[10px] !shadow-[unset] border border-gray-300',
                      {
                        'text-purple-700 bg-purple-50': statusValue === item.param,
                        '!text-gray-700 !bg-white hover:!text-opacity-60':
                          statusValue !== item.param,
                      }
                    )}
                    onClick={() => handleStatusChange(item.param)}
                    data-testid={`players-status-button-${item.param}`}
                  >
                    {item.name}
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
        {showSearchInput ? (
          <span className='relative flex items-center' data-testid='players-event-search'>
            <DebounceInput
              type='text'
              className='w-full rounded-lg shadow-xs leading-7 border border-gray-300 bg-white pl-8 pr-2 py-1.5 text-xs text-gray-500 focus:border-gray-200 focus:outline-none focus:ring focus:ring-gray-100 sm:w-[220px]'
              placeholder={`${config.searchPlaceholder}`}
              minLength={2}
              debounceTimeout={500}
              value={search}
              onChange={handleSearchChange}
              maxLength={100}
              data-testid={`players-search-input`}
            />
            <FontAwesomeIcon
              icon={faSearch}
              className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500'
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
